import { commandRequest } from '../../../agents/context/ui-command-registry.js';
import { useTranslation } from '@govard/i18n-core/react';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { useEffect, useMemo, useState, type ReactElement } from 'react';

import { isOpenRouterChatModel } from '@govard/llm-gate-client';
import type { Scope } from '@govard/tenant-core';
import { Alert, Button as AntButton, Popover, message } from 'antd';
import { PlusOutlined, SettingOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { Button } from '@govard/ui-primitives';

import { AntDesignXChatSurface } from './AntDesignXChatSurface.js';
import { AgentCapabilitiesCard } from './AgentCapabilitiesCard.js';
import { ConversationSwitcher } from './ConversationSwitcher.js';
import { DealChatCrudActions } from './DealChatCrudActions.js';
import { AutoApproveToggle } from './AutoApproveToggle.js';
import { ModelGatewaySelector } from './ModelGatewaySelector.js';
import { DEFAULT_CHAT_GATEWAY, isAvailableChatGateway } from '../lib/available-chat-gateways.js';
import { ReasoningModeSwitch } from './ReasoningModeSwitch.js';
import { useOpenRouterModel } from './OpenRouterModelSelector.js';
import { useChatAgentCapabilities } from '../hooks/useChatAgentCapabilities.js';
import { useChatConversations } from '../hooks/useChatConversations.js';
import { useDealChat } from '../hooks/useDealChat.js';
import { useEntityChat, type EntityChatContextType } from '../hooks/useEntityChat.js';
import { useChatActionHandlers } from '../hooks/useChatActionHandlers.js';
import type { ChatConversationItem, LlmGatewayProfile } from '../api/chat.client.js';

export interface DealChatPanelProps {
  scope: Scope | null;
  dealId: string;
}

export interface EntityChatPanelProps {
  scope: Scope | null;
  contextEntityType: EntityChatContextType;
  contextEntityId: string;
}

export interface ProjectChatPanelProps {
  scope: Scope | null;
  projectId: string;
}

export function ProjectChatPanel({ scope, projectId }: ProjectChatPanelProps): ReactElement {
  return <EntityChatPanel scope={scope} contextEntityType="project" contextEntityId={projectId} />;
}

export function EntityChatPanel({
  scope,
  contextEntityType,
  contextEntityId,
}: EntityChatPanelProps): ReactElement {
  useI18nSubscription();
  const { t } = useTranslation();
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [conversationsOpen, setConversationsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [openRouterModel, setOpenRouterModel] = useOpenRouterModel();
  const [newConversationGateway, setNewConversationGateway] =
    useState<LlmGatewayProfile>(DEFAULT_CHAT_GATEWAY);
  const conversations = useChatConversations(scope, {
    contextEntityType,
    contextEntityId,
  });
  const capabilities = useChatAgentCapabilities(scope, {
    contextEntityType,
    contextEntityId,
  });
  const selectedConversation = conversations.conversations.find(
    (conversation) => conversation.id === selectedConversationId,
  );
  const agent = useEntityChat(scope, {
    contextEntityType,
    contextEntityId,
    ...(selectedConversationId !== null ? { conversationId: selectedConversationId } : {}),
    gatewayProfile: selectedConversation?.gatewayProfile ?? newConversationGateway,
    ...((selectedConversation?.gatewayProfile ?? newConversationGateway) === 'openrouter'
      ? { openRouterModel }
      : {}),
  });
  const gatewayUnavailable =
    selectedConversation !== undefined &&
    !isAvailableChatGateway(selectedConversation.gatewayProfile);
  const chatDisabled =
    scope === null ||
    conversations.isUpdatingGateway ||
    gatewayUnavailable ||
    (selectedConversationId !== null && selectedConversation === undefined);

  useEffect(() => {
    setSelectedConversationId(null);
  }, [contextEntityId, contextEntityType, scope?.workspaceId, scope?.userId]);

  useEffect(() => {
    if (selectedConversationId !== null || conversations.conversations.length === 0) {
      return;
    }

    setSelectedConversationId(conversations.conversations[0]?.id ?? null);
  }, [conversations.conversations, selectedConversationId]);

  const createConversation = (): void => {
    void conversations
      .createConversation({
        contextEntityType,
        contextEntityId,
        gatewayProfile: newConversationGateway,
      })
      .then((conversation) => {
        setSelectedConversationId(conversation.id);
        setConversationsOpen(false);
      });
  };

  const commandTarget =
    contextEntityType === 'project'
      ? { projectId: contextEntityId }
      : contextEntityType === 'deal'
        ? { dealId: contextEntityId }
        : { contactId: contextEntityId };
  const actionHandlers = useChatActionHandlers({
    agent,
    dealId: contextEntityType === 'deal' ? contextEntityId : undefined,
  });

  const conversationPicker = (
    <div style={{ width: 'min(420px, calc(100vw - 32px))' }}>
      <ConversationSwitcher
        conversations={conversations.conversations}
        value={selectedConversationId}
        isLoading={conversations.isLoading}
        disabled={scope === null || agent.isStreaming || conversations.isCreating}
        currentUserId={scope?.userId ?? null}
        isDeleting={conversations.isDeleting}
        onChange={(conversationId) => {
          setSelectedConversationId(conversationId);
          setConversationsOpen(false);
        }}
        onDelete={async (conversationIds) => {
          await conversations.deleteConversations(conversationIds);
          if (selectedConversationId !== null && conversationIds.includes(selectedConversationId)) {
            setSelectedConversationId(null);
          }
        }}
      />
    </div>
  );

  const settings = (
    <div
      className="space-y-4 overflow-y-auto p-1"
      style={{ maxHeight: '70vh', width: 'min(440px, calc(100vw - 32px))' }}
    >
      <ReasoningModeSwitch />
      <AutoApproveToggle
        checked={selectedConversation?.autoApprove === true}
        disabled={
          selectedConversationId === null ||
          selectedConversation?.userId !== scope?.userId ||
          scope === null ||
          agent.isStreaming
        }
        onChange={(enabled) => {
          if (selectedConversationId !== null) {
            void conversations
              .updateAutoApprove(selectedConversationId, enabled)
              .catch(() => message.error(i18n.t('crm.ai_chat.auto_approve.update_failed')));
          }
        }}
      />
      <div className="rounded-xl border border-[var(--ant-color-border)] bg-[var(--ant-color-bg-container)] p-4">
        <p className="mb-3 text-[12px] leading-5 opacity-80">{t('crm.chat.context_hint')}</p>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="w-full justify-center border-[var(--ant-color-border)] bg-[var(--ant-color-fill-quaternary)] text-inherit hover:bg-[var(--ant-control-item-bg-hover)]"
          disabled={agent.isStreaming || chatDisabled}
          onClick={() => {
            void agent.send(commandRequest('quickFollowUp', commandTarget));
          }}
        >
          {t('crm.chat.quick_follow_up')}
        </Button>
      </div>
      <AgentCapabilitiesCard
        capabilities={capabilities.capabilities}
        summary={capabilities.summary}
        isLoading={capabilities.isLoading}
      />
      <div className="rounded-xl border border-[var(--ant-color-border)] bg-[var(--ant-color-bg-container)] p-4">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide opacity-80">
          {t('crm.chat.suggestions_title')}
        </p>
        <div className="space-y-2">
          {contextEntityType === 'project' ? (
            <>
              <button
                type="button"
                className="w-full rounded-lg border border-[var(--ant-color-border)] bg-[var(--ant-color-fill-quaternary)] px-3 py-2 text-left text-[12px] font-medium text-inherit transition hover:bg-[var(--ant-control-item-bg-hover)]"
                disabled={agent.isStreaming || chatDisabled}
                onClick={() => {
                  void agent.send(commandRequest('changes', commandTarget));
                }}
              >
                {t('crm.projects.copilot.suggestion_changes')}
              </button>
              <button
                type="button"
                className="w-full rounded-lg border border-[var(--ant-color-border)] bg-[var(--ant-color-fill-quaternary)] px-3 py-2 text-left text-[12px] font-medium text-inherit transition hover:bg-[var(--ant-control-item-bg-hover)]"
                disabled={agent.isStreaming || chatDisabled}
                onClick={() => {
                  void agent.send(commandRequest('nextStep', commandTarget));
                }}
              >
                {t('crm.projects.copilot.suggestion_next_steps')}
              </button>
              <button
                type="button"
                className="w-full rounded-lg border border-[var(--ant-color-border)] bg-[var(--ant-color-fill-quaternary)] px-3 py-2 text-left text-[12px] font-medium text-inherit transition hover:bg-[var(--ant-control-item-bg-hover)]"
                disabled={agent.isStreaming || chatDisabled}
                onClick={() => {
                  void agent.send(commandRequest('projectBrief', commandTarget));
                }}
              >
                {t('crm.projects.copilot.suggestion_brief')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="w-full rounded-lg border border-[var(--ant-color-border)] bg-[var(--ant-color-fill-quaternary)] px-3 py-2 text-left text-[12px] font-medium text-inherit transition hover:bg-[var(--ant-control-item-bg-hover)]"
                disabled={agent.isStreaming || chatDisabled}
                onClick={() => {
                  void agent.send(commandRequest('nextStep', commandTarget));
                }}
              >
                {t('crm.chat.suggestion_next_step')}
              </button>
              <button
                type="button"
                className="w-full rounded-lg border border-[var(--ant-color-border)] bg-[var(--ant-color-fill-quaternary)] px-3 py-2 text-left text-[12px] font-medium text-inherit transition hover:bg-[var(--ant-control-item-bg-hover)]"
                disabled={agent.isStreaming || chatDisabled}
                onClick={() => {
                  void agent.send(commandRequest('risks', commandTarget));
                }}
              >
                {t('crm.chat.suggestion_risks')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="chatpanel-container" className="flex h-full min-h-0 flex-col bg-white">
      <div className="flex flex-none flex-wrap items-center gap-2 border-b border-stone-200 px-4 py-3">
        <Popover
          trigger="click"
          placement="bottomLeft"
          open={conversationsOpen}
          onOpenChange={setConversationsOpen}
          content={conversationPicker}
        >
          <AntButton icon={<UnorderedListOutlined />}>{t('crm.chat.conversations')}</AntButton>
        </Popover>
        <AntButton
          type="primary"
          icon={<PlusOutlined />}
          loading={conversations.isCreating}
          disabled={scope === null || agent.isStreaming}
          onClick={createConversation}
        >
          {t('crm.chat.new_conversation')}
        </AntButton>
        <ModelGatewaySelector
          gateway={selectedConversation?.gatewayProfile ?? newConversationGateway}
          openRouterModel={openRouterModel}
          disabled={
            (selectedConversationId !== null && selectedConversation?.userId !== scope?.userId) ||
            scope === null ||
            agent.isStreaming ||
            conversations.isUpdatingGateway
          }
          onGatewayChange={(gatewayProfile) => {
            setNewConversationGateway(gatewayProfile);
            if (selectedConversationId !== null) {
              void conversations
                .updateGateway(selectedConversationId, gatewayProfile)
                .catch(() =>
                  message.error(
                    i18n.t(
                      'crm.features.features.ai.chat.components.chatpanel.ne_udalos_izmenit_ai_shlyuz',
                    ),
                  ),
                );
            }
          }}
          onOpenRouterModelChange={setOpenRouterModel}
        />
        <Popover
          trigger="click"
          placement="bottomRight"
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          content={settings}
        >
          <AntButton data-testid="chat-settings" className="ml-auto" icon={<SettingOutlined />}>
            {t('crm.chat.settings')}
          </AntButton>
        </Popover>
      </div>
      {gatewayUnavailable && (
        <Alert
          type="warning"
          showIcon
          className="mx-4 mt-3"
          title={t('crm.ai_chat.model_selector.unavailable_hint')}
        />
      )}
      <div className="min-h-0 flex-1 p-4">
        <AntDesignXChatSurface
          agent={agent}
          placeholder={t('crm.chat.placeholder')}
          submitLabel={t('crm.chat.send')}
          disabled={chatDisabled}
          conversationId={selectedConversationId}
          draftScope={scope ?? undefined}
          onDecideActionPlanItem={agent.decideActionPlanItem}
          onRequestActionPlanUndo={agent.requestActionPlanUndo}
          onRequestActionPlanRetry={agent.requestActionPlanRetry}
          onRequestModelUpgrade={(offer) => {
            if (isOpenRouterChatModel(offer.fallbackModel)) {
              const fallbackModel = offer.fallbackModel;
              if (
                selectedConversationId !== null &&
                selectedConversation?.gatewayProfile !== 'openrouter'
              ) {
                void conversations
                  .updateGateway(selectedConversationId, 'openrouter')
                  .then(() => setOpenRouterModel(fallbackModel))
                  .catch(() =>
                    message.error(
                      i18n.t(
                        'crm.features.features.ai.chat.components.chatpanel.ne_udalos_izmenit_ai_shlyuz',
                      ),
                    ),
                  );
              } else {
                setOpenRouterModel(fallbackModel);
              }
            }
          }}
          contextEntityType={contextEntityType}
          contextEntityId={contextEntityId}
          onSendEmailDraft={chatDisabled ? undefined : actionHandlers.onSendEmailDraft}
          onPrepareDealFollowUp={chatDisabled ? undefined : actionHandlers.onPrepareDealFollowUp}
          onProposeTouchStrategy={chatDisabled ? undefined : actionHandlers.onProposeTouchStrategy}
          onCreateDealTasks={chatDisabled ? undefined : actionHandlers.onCreateDealTasks}
          onCreateMeetingTasks={chatDisabled ? undefined : actionHandlers.onCreateMeetingTasks}
          onPrepareMeetingFollowUp={
            chatDisabled ? undefined : actionHandlers.onPrepareMeetingFollowUp
          }
        />
      </div>
    </div>
  );
}

export function DealChatPanel({ scope, dealId }: DealChatPanelProps): ReactElement {
  const { t } = useTranslation();
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [createdConversation, setCreatedConversation] = useState<ChatConversationItem | null>(null);
  const [openRouterModel, setOpenRouterModel] = useOpenRouterModel();
  const conversations = useChatConversations(scope, {
    contextEntityType: 'deal',
    contextEntityId: dealId,
  });
  const capabilities = useChatAgentCapabilities(scope, {
    contextEntityType: 'deal',
    contextEntityId: dealId,
  });
  const selectedConversation = useMemo(
    () =>
      selectedConversationId === null
        ? null
        : (conversations.conversations.find(
            (conversation) => conversation.id === selectedConversationId,
          ) ?? (createdConversation?.id === selectedConversationId ? createdConversation : null)),
    [conversations.conversations, createdConversation, selectedConversationId],
  );
  const [newConversationGateway, setNewConversationGateway] =
    useState<LlmGatewayProfile>(DEFAULT_CHAT_GATEWAY);
  // Until a conversation is selected the first turn creates it, so the route
  // shown in the selector (newConversationGateway) must travel with that turn.
  const effectiveGateway: LlmGatewayProfile | undefined =
    selectedConversationId === null ? newConversationGateway : selectedConversation?.gatewayProfile;
  const agent = useDealChat(
    scope,
    dealId,
    selectedConversationId,
    effectiveGateway === 'openrouter' ? openRouterModel : undefined,
    selectedConversationId === null ? newConversationGateway : undefined,
  );
  const gatewayUnavailable =
    selectedConversation !== null && !isAvailableChatGateway(selectedConversation.gatewayProfile);
  const chatDisabled =
    scope === null ||
    conversations.isUpdatingGateway ||
    gatewayUnavailable ||
    (selectedConversationId !== null && selectedConversation === null);
  const [conversationsOpen, setConversationsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const createConversation = (): void => {
    void conversations
      .createConversation({
        contextEntityType: 'deal',
        contextEntityId: dealId,
        gatewayProfile: newConversationGateway,
      })
      .then((conversation) => {
        setCreatedConversation(conversation);
        setSelectedConversationId(conversation.id);
        setConversationsOpen(false);
      });
  };

  useEffect(() => {
    setCreatedConversation(null);
    setSelectedConversationId(null);
  }, [dealId, scope?.workspaceId, scope?.userId]);

  useEffect(() => {
    if (
      createdConversation !== null &&
      conversations.conversations.some((conversation) => conversation.id === createdConversation.id)
    ) {
      setCreatedConversation(null);
    }
  }, [conversations.conversations, createdConversation]);

  useEffect(() => {
    if (selectedConversationId !== null || conversations.conversations.length === 0) {
      return;
    }

    setSelectedConversationId(conversations.conversations[0]?.id ?? null);
  }, [conversations.conversations, selectedConversationId]);

  useEffect(() => {
    if (
      selectedConversation?.gatewayProfile &&
      isAvailableChatGateway(selectedConversation.gatewayProfile)
    ) {
      setNewConversationGateway(selectedConversation.gatewayProfile);
    }
  }, [selectedConversation?.gatewayProfile]);

  const conversationPicker = (
    <div style={{ width: 'min(420px, calc(100vw - 32px))' }}>
      <ConversationSwitcher
        conversations={conversations.conversations}
        value={selectedConversationId}
        isLoading={conversations.isLoading}
        disabled={scope === null || agent.isStreaming || conversations.isCreating}
        currentUserId={scope?.userId ?? null}
        isDeleting={conversations.isDeleting}
        onChange={(conversationId) => {
          if (conversationId !== createdConversation?.id) {
            setCreatedConversation(null);
          }
          setSelectedConversationId(conversationId);
          setConversationsOpen(false);
        }}
        onDelete={async (conversationIds) => {
          await conversations.deleteConversations(conversationIds);
          if (selectedConversationId !== null && conversationIds.includes(selectedConversationId)) {
            setCreatedConversation(null);
            setSelectedConversationId(null);
          }
        }}
      />
    </div>
  );

  const settings = (
    <div
      className="space-y-4 overflow-y-auto p-1"
      style={{ maxHeight: '70vh', width: 'min(440px, calc(100vw - 32px))' }}
    >
      <ReasoningModeSwitch />
      <AutoApproveToggle
        checked={selectedConversation?.autoApprove === true}
        disabled={
          selectedConversationId === null ||
          selectedConversation?.userId !== scope?.userId ||
          scope === null ||
          agent.isStreaming
        }
        onChange={(enabled) => {
          if (selectedConversationId !== null) {
            void conversations
              .updateAutoApprove(selectedConversationId, enabled)
              .catch(() => message.error(i18n.t('crm.ai_chat.auto_approve.update_failed')));
          }
        }}
      />
      <div className="rounded-xl border border-[var(--ant-color-border)] bg-[var(--ant-color-bg-container)] p-4">
        <p className="mb-3 text-[12px] leading-5 opacity-80">{t('crm.chat.context_hint')}</p>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="w-full justify-center border-[var(--ant-color-border)] bg-[var(--ant-color-fill-quaternary)] text-inherit hover:bg-[var(--ant-control-item-bg-hover)]"
          disabled={agent.isStreaming || chatDisabled}
          onClick={() => {
            void agent.send(commandRequest('quickFollowUp', { dealId }));
          }}
        >
          {t('crm.chat.quick_follow_up')}
        </Button>
      </div>
      <AgentCapabilitiesCard
        capabilities={capabilities.capabilities}
        summary={capabilities.summary}
        isLoading={capabilities.isLoading}
      />
      <DealChatCrudActions agent={agent} dealId={dealId} disabled={chatDisabled} />
      <div className="rounded-xl border border-[var(--ant-color-border)] bg-[var(--ant-color-bg-container)] p-4">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide opacity-80">
          {t('crm.chat.suggestions_title')}
        </p>
        <div className="space-y-2">
          <button
            type="button"
            className="w-full rounded-lg border border-[var(--ant-color-border)] bg-[var(--ant-color-fill-quaternary)] px-3 py-2 text-left text-[12px] font-medium text-inherit transition hover:bg-[var(--ant-control-item-bg-hover)]"
            disabled={agent.isStreaming || chatDisabled}
            onClick={() => {
              void agent.send(commandRequest('dealCockpit', { dealId }));
            }}
          >
            {t('crm.chat.suggestion_cockpit')}
          </button>
          <button
            type="button"
            className="w-full rounded-lg border border-[var(--ant-color-border)] bg-[var(--ant-color-fill-quaternary)] px-3 py-2 text-left text-[12px] font-medium text-inherit transition hover:bg-[var(--ant-control-item-bg-hover)]"
            disabled={agent.isStreaming || chatDisabled}
            onClick={() => {
              void agent.send(commandRequest('nextStep', { dealId }));
            }}
          >
            {t('crm.chat.suggestion_next_step')}
          </button>
          <button
            type="button"
            className="w-full rounded-lg border border-[var(--ant-color-border)] bg-[var(--ant-color-fill-quaternary)] px-3 py-2 text-left text-[12px] font-medium text-inherit transition hover:bg-[var(--ant-control-item-bg-hover)]"
            disabled={agent.isStreaming || chatDisabled}
            onClick={() => {
              void agent.send(commandRequest('actionPlan', { dealId }));
            }}
          >
            {t('crm.chat.suggestion_action_plan')}
          </button>
          <button
            type="button"
            className="w-full rounded-lg border border-[var(--ant-color-border)] bg-[var(--ant-color-fill-quaternary)] px-3 py-2 text-left text-[12px] font-medium text-inherit transition hover:bg-[var(--ant-control-item-bg-hover)]"
            disabled={agent.isStreaming || chatDisabled}
            onClick={() => {
              void agent.send(commandRequest('risks', { dealId }));
            }}
          >
            {t('crm.chat.suggestion_risks')}
          </button>
        </div>
      </div>
    </div>
  );

  const actionHandlers = useChatActionHandlers({
    agent,
    dealId,
  });

  return (
    <div data-testid="chatpanel-container" className="flex h-full min-h-0 flex-col bg-white">
      <div className="flex flex-none flex-wrap items-center gap-2 border-b border-stone-200 px-4 py-3">
        <Popover
          trigger="click"
          placement="bottomLeft"
          open={conversationsOpen}
          onOpenChange={setConversationsOpen}
          content={conversationPicker}
        >
          <AntButton icon={<UnorderedListOutlined />}>{t('crm.chat.conversations')}</AntButton>
        </Popover>
        <AntButton
          type="primary"
          icon={<PlusOutlined />}
          loading={conversations.isCreating}
          disabled={scope === null || agent.isStreaming}
          onClick={createConversation}
        >
          {t('crm.chat.new_conversation')}
        </AntButton>
        <ModelGatewaySelector
          gateway={selectedConversation?.gatewayProfile ?? newConversationGateway}
          openRouterModel={openRouterModel}
          disabled={
            scope === null ||
            agent.isStreaming ||
            conversations.isUpdatingGateway ||
            (selectedConversationId !== null && selectedConversation?.userId !== scope?.userId)
          }
          onGatewayChange={(gatewayProfile) => {
            setNewConversationGateway(gatewayProfile);
            if (selectedConversationId !== null) {
              void conversations
                .updateGateway(selectedConversationId, gatewayProfile)
                .catch(() =>
                  message.error(
                    i18n.t(
                      'crm.features.features.ai.chat.components.chatpanel.ne_udalos_izmenit_ai_shlyuz_2',
                    ),
                  ),
                );
            }
          }}
          onOpenRouterModelChange={setOpenRouterModel}
        />
        <Popover
          trigger="click"
          placement="bottomRight"
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          content={settings}
        >
          <AntButton data-testid="chat-settings" className="ml-auto" icon={<SettingOutlined />}>
            {t('crm.chat.settings')}
          </AntButton>
        </Popover>
      </div>
      {gatewayUnavailable && (
        <Alert
          type="warning"
          showIcon
          className="mx-4 mt-3"
          title={t('crm.ai_chat.model_selector.unavailable_hint')}
        />
      )}
      <div className="min-h-0 flex-1 p-4">
        <AntDesignXChatSurface
          agent={agent}
          placeholder={t('crm.chat.placeholder')}
          submitLabel={t('crm.chat.send')}
          disabled={chatDisabled}
          conversationId={selectedConversationId}
          draftScope={scope ?? undefined}
          onDecideActionPlanItem={agent.decideActionPlanItem}
          onRequestActionPlanUndo={agent.requestActionPlanUndo}
          onRequestActionPlanRetry={agent.requestActionPlanRetry}
          onRequestModelUpgrade={(offer) => {
            if (isOpenRouterChatModel(offer.fallbackModel)) {
              const fallbackModel = offer.fallbackModel;
              setNewConversationGateway('openrouter');
              if (
                selectedConversationId !== null &&
                selectedConversation?.gatewayProfile !== 'openrouter'
              ) {
                void conversations
                  .updateGateway(selectedConversationId, 'openrouter')
                  .then(() => setOpenRouterModel(fallbackModel))
                  .catch(() =>
                    message.error(
                      i18n.t(
                        'crm.features.features.ai.chat.components.chatpanel.ne_udalos_izmenit_ai_shlyuz_2',
                      ),
                    ),
                  );
              } else {
                setOpenRouterModel(fallbackModel);
              }
            }
          }}
          contextEntityType="deal"
          contextEntityId={dealId}
          onExecutePrompt={chatDisabled ? undefined : (prompt) => agent.send(prompt)}
          onSendEmailDraft={chatDisabled ? undefined : actionHandlers.onSendEmailDraft}
          onPrepareDealFollowUp={chatDisabled ? undefined : actionHandlers.onPrepareDealFollowUp}
          onProposeTouchStrategy={chatDisabled ? undefined : actionHandlers.onProposeTouchStrategy}
          onCreateDealTasks={chatDisabled ? undefined : actionHandlers.onCreateDealTasks}
          onCreateMeetingTasks={chatDisabled ? undefined : actionHandlers.onCreateMeetingTasks}
          onPrepareMeetingFollowUp={
            chatDisabled ? undefined : actionHandlers.onPrepareMeetingFollowUp
          }
        />
      </div>
      {agent.usage ? (
        <p className="flex-none px-4 pb-2 text-xs text-muted-foreground">
          {t('crm.chat.usage', { cost: agent.usage.costRub.toFixed(1) })}
        </p>
      ) : null}
    </div>
  );
}
